import * as React from "react";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { OverrunBadge, isOverrun } from "@/features/oee/OverrunBadge";

export const V_LABEL =
  "text-[10.5px] font-semibold uppercase tracking-[0.07em] text-text-secondary";
export const V_NUM = "font-mono text-[30px] font-medium leading-none tracking-[-0.03em]";
export const V_NUM_UNIT = "text-[16px] text-text-secondary";
export const V_NUM_SLASH = "text-text-tertiary";
export const V_SUB = "text-[11.5px] text-text-secondary";

function fmtInt(v: number | null | undefined): string {
  return v != null && Number.isFinite(v) ? v.toLocaleString("pt-BR") : "—";
}

export function MicroBar({
  label,
  value,
  color,
  onInfo,
  infoAriaLabel,
}: {
  label: string;
  value: number | null | undefined;
  color: string;
  onInfo?: () => void;
  infoAriaLabel?: string;
}): React.ReactElement {
  const v = value ?? 0;
  const pct = Math.max(0, Math.min(100, v));
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1 flex justify-between text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">
        <span className="inline-flex items-center gap-1">
          {label}
          {onInfo ? (
            <button
              type="button"
              onClick={onInfo}
              aria-label={infoAriaLabel ?? `Explicar cálculo de ${label}`}
              className="rounded p-0.5 normal-case tracking-normal text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
            >
              <Info className="h-3 w-3" />
            </button>
          ) : null}
        </span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <b className="font-mono font-medium text-text-primary">{fmtInt(Math.round(v))}</b>
          {/* O hero clampa a barra em 100% igual às demais, então 125% e 100%
              desenhavam idêntico aqui também. */}
          <OverrunBadge value={value} metricLabel={label} />
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-bg-muted-rd">
        <div
          className={cn(
            "h-full w-[var(--micro-w)] rounded-full bg-[color:var(--micro-color)]",
            isOverrun(value) && "rounded-r-none"
          )}
          style={{ "--micro-w": `${pct}%`, "--micro-color": color } as React.CSSProperties}
        />
      </div>
    </div>
  );
}

export function VitalCell({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-2 border-b border-border-subtle px-5 py-[18px]",
        "lg:border-b-0 lg:border-r lg:last:border-r-0",
        className
      )}
    >
      <div className={V_LABEL}>{label}</div>
      {children}
    </div>
  );
}
